/**
 * Procedural runway rendering: pavement, markings, lights and PAPI.
 *
 * ## One texture, one quad
 *
 * The obvious way to draw runway markings is a mesh per stripe — a centreline dash here, a
 * threshold bar there. KSFO has four runways; done that way it is several hundred draw calls
 * for an airport, against a budget of 350 for the *entire frame*. So every marking a runway
 * carries — centreline, threshold piano keys, designator numbers, aiming points, touchdown
 * zone bars, edge stripes — is painted once into a single canvas at bake time and the whole
 * runway is one textured quad. An airport becomes a handful of draw calls instead of hundreds,
 * and the markings get anti-aliasing and mipmaps for free.
 *
 * The canvas is authored in runway coordinates — across × along, at a fixed metres-per-pixel —
 * so a 500 m altiport and a 4 km international runway both come out with correctly *sized*
 * markings rather than correctly *proportioned* ones. Real runway markings are specified in
 * metres, not in fractions of the runway.
 *
 * ## Slope
 *
 * Threshold elevations are carried per end, so the pavement is built as a ramp between them.
 * For most airports the difference is a metre or two and invisible. For Courchevel it is 100 m
 * over 537 m of runway — an 18.5 % gradient that is the entire character of the place, and
 * which a flat quad would throw away.
 *
 * ## The ground pad
 *
 * Streamed DEM tiles know nothing about airports, so the terrain under a runway is whatever
 * SRTM sampled at 30 m — lumpy, and quite capable of putting a hill through the tarmac. The
 * runway therefore sits on a *pad*: a flat apron at field elevation, extending well past the
 * pavement, with a skirt dropping from its edge to hide the seam where it meets real ground.
 * That is a stand-in for true DEM flattening (which belongs in the tile mesher and needs the
 * airport database in the worker) and it is convincing from anywhere a pilot actually looks.
 *
 * `updateFrame(alpha)`: yes — the PAPI is recomputed from the viewer's position, and the
 * lights fade with the sun.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  DoubleSide,
  Group,
  LinearFilter,
  Mesh,
  Points,
  Quaternion,
  RepeatWrapping,
  Vector3,
} from 'three';
import { MeshStandardNodeMaterial, PointsNodeMaterial } from 'three/webgpu';
import { DEG2RAD, RAD2DEG, clamp } from '../../core/math.js';
import { LocalFrame } from '../geodesy.js';
import type { Airport, Runway } from './db.js';
/*
 * The pad's dimensions come from `pad.ts`, which is also what the flight model stands on. They
 * used to be private constants here, and the flight model used the streamed DEM instead — so the
 * aeroplane sat half a metre inside its own runway. One definition, two consumers, no drift.
 */
import { PAD_MARGIN, PAD_SKIRT_M, PAVEMENT_LIFT_M, padOverrunM } from './pad.js';


/**
 * PAPI transition angles, shallowest unit first [deg].
 *
 * Module scope rather than rebuilt per frame: the four-element literal used to be constructed on
 * every airport's update, and a dynamic index into it (`[i % 4]`) is exactly the shape that stops
 * V8 scalar-replacing the array, so it was genuinely allocated.
 */
const PAPI_TRANSITION_DEG = [3.5, 3.17, 2.83, 2.5] as const;

/** Reused: the vector from a PAPI unit to the viewer, rebuilt per unit per frame. */
const scratchToViewer = /* #__PURE__ */ new Vector3();

/** Metres per pixel in the marking canvas. 0.25 m resolves a 15 cm stripe edge cleanly. */
const MARKING_M_PER_PX = 0.25;
/** Ceiling on the canvas' long edge [px], so a 5 km runway does not ask for a 20k texture. */
const MAX_MARKING_PX = 8192;
/**
 * Floor on the canvas' short edge [px].
 *
 * A runway is 60 m of width to 3.6 km of length, so a canvas at the runway's own aspect ratio
 * comes out 136 px across — and every marking that runs *across* the runway, the piano keys and
 * the designator especially, ends up a handful of pixels wide and unreadable from the flare.
 * The across axis is therefore over-sampled independently; the UVs do not care.
 */
const MIN_MARKING_ACROSS_PX = 256;
/** Spacing of runway edge lights [m]. Real spacing is 60 m. */
const EDGE_LIGHT_SPACING_M = 60;

const surfaceColors: Record<string, number> = {
  asphalt: 0x35383c,
  concrete: 0x63666a,
  grass: 0x53663a,
  gravel: 0x6b6358,
  dirt: 0x6d5a44,
  water: 0x1d3a4a,
  unknown: 0x4a4d52,
};

/**
 * Paint every marking a runway carries into one canvas.
 *
 * Dimensions and layout follow ICAO Annex 14 / FAA AC 150/5340-1 closely enough to look right
 * to anyone who has seen a runway: 30 m centreline stripes on 20 m gaps, piano keys 30 m long
 * starting 6 m in from the threshold, the designator 12 m tall, aiming points 45 m long at
 * 300 m from each threshold, and touchdown-zone bars beyond them.
 */
export function paintRunwayMarkings(runway: Runway): HTMLCanvasElement {
  const alongPx = Math.min(MAX_MARKING_PX, Math.round(runway.lengthM / MARKING_M_PER_PX));
  const acrossPx = Math.max(
    MIN_MARKING_ACROSS_PX,
    Math.round((runway.widthM / runway.lengthM) * alongPx),
  );
  // Metres per pixel differs between the two axes now, so markings are laid out in the along
  // axis' scale and the across axis is scaled separately where it matters.
  const mPerPx = runway.lengthM / alongPx;
  const mPerPxAcross = runway.widthM / acrossPx;

  const canvas = document.createElement('canvas');
  canvas.width = acrossPx;
  canvas.height = alongPx;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return canvas;

  const m = (metres: number): number => metres / mPerPx;
  const mx = (metres: number): number => metres / mPerPxAcross;
  const paved = runway.surface === 'asphalt' || runway.surface === 'concrete';

  ctx.fillStyle = `#${surfaceColors[runway.surface]?.toString(16).padStart(6, '0') ?? '4a4d52'}`;
  ctx.fillRect(0, 0, acrossPx, alongPx);

  // A little longitudinal streaking: real pavement is laid in strips and stained by rubber.
  ctx.globalAlpha = 0.09;
  for (let x = 0; x < acrossPx; x += Math.max(2, Math.round(mx(6)))) {
    ctx.fillStyle = (x / Math.max(2, Math.round(mx(6)))) % 2 === 0 ? '#ffffff' : '#000000';
    ctx.fillRect(x, 0, Math.max(1, Math.round(mx(3))), alongPx);
  }
  ctx.globalAlpha = 1;

  if (!paved) return canvas; // grass and gravel strips carry no painted markings

  const white = '#e6e9ec';
  ctx.fillStyle = white;
  const centre = acrossPx / 2;

  // --- edge stripes: 0.9 m wide, 3 m in from the edge -------------------------------
  const edgeInset = mx(3);
  const edgeWidth = Math.max(1, mx(0.9));
  ctx.fillRect(edgeInset, 0, edgeWidth, alongPx);
  ctx.fillRect(acrossPx - edgeInset - edgeWidth, 0, edgeWidth, alongPx);

  // --- centreline: 30 m stripes, 20 m gaps ------------------------------------------
  const stripeWidth = Math.max(1, mx(0.9));
  for (let along = m(60); along < alongPx - m(60); along += m(50)) {
    ctx.fillRect(centre - stripeWidth / 2, along, stripeWidth, m(30));
  }

  // --- threshold piano keys, both ends -----------------------------------------------
  // Stripe count follows the runway width: 4 at 18 m up to 16 at 60 m.
  const keyCount = clamp(Math.round(runway.widthM / 5) * 2, 4, 16);
  const keyWidth = mx(1.8);
  const keyGap = mx(1.8);
  const keySpan = keyCount * keyWidth + (keyCount - 1) * keyGap;
  for (const end of [0, 1]) {
    const start = end === 0 ? m(6) : alongPx - m(6) - m(30);
    for (let k = 0; k < keyCount; k++) {
      const x = centre - keySpan / 2 + k * (keyWidth + keyGap);
      ctx.fillRect(x, start, keyWidth, m(30));
    }
  }

  // --- aiming points: 45 m bars, 300 m from each threshold ----------------------------
  if (runway.lengthM > 1_200) {
    const aimWidth = mx(6);
    const aimOffset = mx(22);
    for (const end of [0, 1]) {
      const along = end === 0 ? m(300) : alongPx - m(300) - m(45);
      ctx.fillRect(centre - aimOffset - aimWidth, along, aimWidth, m(45));
      ctx.fillRect(centre + aimOffset, along, aimWidth, m(45));
    }
  }

  // --- touchdown zone bars -------------------------------------------------------------
  if (runway.lengthM > 2_400) {
    const tdWidth = mx(1.8);
    const tdLength = m(22.5);
    for (const end of [0, 1]) {
      for (let pair = 1; pair <= 3; pair++) {
        const distance = m(150 + pair * 150);
        const along = end === 0 ? distance : alongPx - distance - tdLength;
        for (const side of [-1, 1]) {
          const x = centre + side * mx(10.5) - (side < 0 ? tdWidth : 0);
          ctx.fillRect(x, along, tdWidth, tdLength);
        }
      }
    }
  }

  // --- designators ----------------------------------------------------------------------
  drawDesignator(ctx, runway.lowIdent, centre, m(60), mx(12), acrossPx, false);
  drawDesignator(ctx, runway.highIdent, centre, alongPx - m(60), mx(12), acrossPx, true);

  return canvas;
}

/**
 * Runway numbers, drawn to be read from the approach.
 *
 * The canvas' `+y` runs from the low threshold toward the high one, so the number at each end
 * has to be rotated to face the aircraft landing on it — the low end reads with the aircraft's
 * direction of travel, the high end reads against it.
 */
function drawDesignator(
  ctx: CanvasRenderingContext2D,
  ident: string,
  centreX: number,
  alongY: number,
  heightPx: number,
  acrossPx: number,
  flipped: boolean,
): void {
  if (ident === '') return;
  ctx.save();
  ctx.translate(centreX, alongY);
  // Numbers face the approaching aircraft: the low end is read heading "up" the canvas.
  ctx.rotate(flipped ? Math.PI / 2 : -Math.PI / 2);
  ctx.fillStyle = '#e6e9ec';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `600 ${Math.max(8, heightPx)}px ui-monospace, monospace`;
  // Squash slightly: real runway characters are tall and narrow.
  ctx.scale(0.72, 1);
  ctx.fillText(ident, 0, 0, acrossPx * 1.2);
  ctx.restore();
}

export interface AirportSceneOptions {
  airport: Airport;
  /** Cast shadows from the runway edges. Off at Low. */
  castShadows?: boolean;
  /**
   * Applied to every ground-plane material the airport builds — the pad and the pavement.
   *
   * A callback rather than a value because what currently wants it lives in `render/`, and this
   * module is in `world/`: a runway that had to know what a volumetric cloud is would be the
   * wrong shape of dependency. It exists because a runway is drawn *on* the terrain, and a
   * kilometre of bright concrete inside a cloud shadow is the one place the effect would visibly
   * stop at an object boundary.
   */
  decorateGroundMaterial?: (material: MeshStandardNodeMaterial) => void;
}

/**
 * The renderable airport: one group holding a pad, a pavement quad and the lights per runway.
 *
 * Built in the airport's own tangent frame, exactly like a terrain tile, so placing it under a
 * moving floating origin is a position and a quaternion rather than a rebuild.
 */
export class AirportScene {
  readonly group = new Group();
  readonly frame: LocalFrame;
  readonly airport: Airport;

  private readonly disposables: Array<{ dispose(): void }> = [];
  private readonly papiLights: Array<{ mesh: Mesh; material: MeshStandardNodeMaterial }> = [];
  private readonly papi: Array<{ runway: Runway; end: 'low' | 'high'; position: Vector3 }> = [];
  private readonly lightMaterials: PointsNodeMaterial[] = [];
  private nightAmount = 0;
  private readonly decorateGroundMaterial: ((material: MeshStandardNodeMaterial) => void) | undefined;

  constructor(options: AirportSceneOptions) {
    const airport = options.airport;
    this.airport = airport;
    this.group.name = `airport-${airport.icao}`;
    this.group.matrixAutoUpdate = false;
    this.frame = new LocalFrame(airport.latitudeDeg, airport.longitudeDeg, 0);
    this.decorateGroundMaterial = options.decorateGroundMaterial;

    for (const runway of airport.runways) {
      this.group.add(this.buildPad(runway));
      this.group.add(this.buildPavement(runway, options.castShadows ?? true));
      const lights = this.buildEdgeLights(runway);
      if (lights !== null) this.group.add(lights);
      this.group.add(this.buildPapi(runway));
    }
  }

  /** Threshold position in the airport's tangent frame (render axes). */
  private thresholdPoint(runway: Runway, end: 'low' | 'high', out: Vector3): Vector3 {
    const latitudeDeg = end === 'low' ? runway.lowLatitudeDeg : runway.highLatitudeDeg;
    const longitudeDeg = end === 'low' ? runway.lowLongitudeDeg : runway.highLongitudeDeg;
    const elevationM = end === 'low' ? runway.lowElevationM : runway.highElevationM;
    this.frame.geodeticToTangent(latitudeDeg, longitudeDeg, elevationM, out);
    // Tangent ENU (east, north, up) → render (x east, y up, z south).
    return out.set(out.x, out.z, -out.y);
  }

  /**
   * The flat apron the runway sits on, with a skirt that dives into whatever the DEM is doing.
   */
  private buildPad(runway: Runway): Mesh {
    const low = this.thresholdPoint(runway, 'low', new Vector3());
    const high = this.thresholdPoint(runway, 'high', new Vector3());
    const along = new Vector3().subVectors(high, low);
    const lengthM = Math.max(1, Math.hypot(along.x, along.z));
    along.normalize();
    const across = new Vector3(-along.z, 0, along.x);

    const halfWidth = runway.widthM * PAD_MARGIN * 0.5;
    const overrun = padOverrunM(runway);

    const positions = new Float32Array(8 * 3);
    const centre = new Vector3().addVectors(low, high).multiplyScalar(0.5);
    const halfLength = lengthM / 2 + overrun;
    // The pad follows the runway's own slope, so a sloping runway does not sit in a flat dish.
    const slope = (high.y - low.y) / lengthM;

    let v = 0;
    for (const [sideAlong, sideAcross] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ] as const) {
      const x = centre.x + along.x * halfLength * sideAlong + across.x * halfWidth * sideAcross;
      const z = centre.z + along.z * halfLength * sideAlong + across.z * halfWidth * sideAcross;
      const y = centre.y + slope * halfLength * sideAlong;
      positions[v++] = x;
      positions[v++] = y;
      positions[v++] = z;
    }
    // The skirt ring, directly below the rim.
    for (let i = 0; i < 4; i++) {
      positions[v++] = positions[i * 3];
      positions[v++] = positions[i * 3 + 1] - PAD_SKIRT_M;
      positions[v++] = positions[i * 3 + 2];
    }

    const indices = new Uint16Array([
      0, 2, 1, 0, 3, 2, // the apron itself
      0, 1, 5, 0, 5, 4, // and a curtain around it
      1, 2, 6, 1, 6, 5,
      2, 3, 7, 2, 7, 6,
      3, 0, 4, 3, 4, 7,
    ]);

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setIndex(new BufferAttribute(indices, 1));
    geometry.computeVertexNormals();

    const material = new MeshStandardNodeMaterial();
    material.color = new Color(runway.surface === 'grass' ? 0x4d6236 : 0x585c4e);
    material.roughness = 0.97;
    material.side = DoubleSide;
    // Draw before the pavement so the runway always wins the depth fight over its own apron.
    material.polygonOffset = true;
    material.polygonOffsetFactor = 1;
    material.polygonOffsetUnits = 1;
    this.decorateGroundMaterial?.(material);

    this.disposables.push(geometry, material);
    const mesh = new Mesh(geometry, material);
    mesh.name = `pad-${runway.lowIdent}`;
    mesh.receiveShadow = true;
    return mesh;
  }

  /** The pavement: one quad carrying every marking as a texture. */
  private buildPavement(runway: Runway, castShadows: boolean): Mesh {
    const low = this.thresholdPoint(runway, 'low', new Vector3());
    const high = this.thresholdPoint(runway, 'high', new Vector3());
    const along = new Vector3().subVectors(high, low);
    const lengthM = Math.max(1, Math.hypot(along.x, along.z));
    along.normalize();
    const across = new Vector3(-along.z, 0, along.x).multiplyScalar(runway.widthM / 2);

    // Lifted a few centimetres so it wins the depth fight with the pad beneath it. The gear
    // applies the same lift, so the wheels rest on the pavement rather than 6 cm inside it.
    const lift = PAVEMENT_LIFT_M;
    const positions = new Float32Array([
      low.x - across.x, low.y + lift, low.z - across.z,
      low.x + across.x, low.y + lift, low.z + across.z,
      high.x + across.x, high.y + lift, high.z + across.z,
      high.x - across.x, high.y + lift, high.z - across.z,
    ]);
    const uvs = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
    const indices = new Uint16Array([0, 2, 1, 0, 3, 2]);

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
    geometry.setIndex(new BufferAttribute(indices, 1));
    geometry.computeVertexNormals();

    const texture = new CanvasTexture(paintRunwayMarkings(runway));
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.minFilter = LinearFilter;
    texture.anisotropy = 8;
    texture.colorSpace = 'srgb';

    const material = new MeshStandardNodeMaterial();
    material.map = texture;
    material.roughness = runway.surface === 'concrete' ? 0.86 : 0.93;
    material.metalness = 0;
    material.side = DoubleSide;
    this.decorateGroundMaterial?.(material);

    this.disposables.push(geometry, material, texture);
    const mesh = new Mesh(geometry, material);
    mesh.name = `runway-${runway.lowIdent}-${runway.highIdent}`;
    mesh.receiveShadow = true;
    mesh.castShadow = castShadows;
    void lengthM;
    return mesh;
  }

  /**
   * Edge lights: one `Points` object per runway, so a fully lit international airport is four
   * draw calls rather than a thousand.
   */
  private buildEdgeLights(runway: Runway): Points | null {
    if (!runway.lighted) return null;

    const low = this.thresholdPoint(runway, 'low', new Vector3());
    const high = this.thresholdPoint(runway, 'high', new Vector3());
    const along = new Vector3().subVectors(high, low);
    const lengthM = Math.max(1, Math.hypot(along.x, along.z));
    const steps = Math.max(2, Math.round(lengthM / EDGE_LIGHT_SPACING_M));
    const across = new Vector3(-along.z, 0, along.x).normalize().multiplyScalar(runway.widthM / 2 + 3);

    // Two edge rows, plus a green threshold bar and a red end bar at each end.
    const count = (steps + 1) * 2 + 12;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    let v = 0;

    const push = (point: Vector3, r: number, g: number, b: number): void => {
      positions[v * 3] = point.x;
      positions[v * 3 + 1] = point.y + 0.35;
      positions[v * 3 + 2] = point.z;
      colors[v * 3] = r;
      colors[v * 3 + 1] = g;
      colors[v * 3 + 2] = b;
      v++;
    };

    const at = new Vector3();
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      at.lerpVectors(low, high, t);
      // White edge lights turn amber over the last 600 m — the "you are running out" cue.
      const remaining = lengthM * (1 - t);
      const amber = remaining < 600 ? 1 : 0;
      for (const side of [-1, 1]) {
        push(
          new Vector3(at.x + across.x * side, at.y, at.z + across.z * side),
          1,
          amber === 1 ? 0.72 : 0.97,
          amber === 1 ? 0.24 : 0.9,
        );
      }
    }
    // Threshold green at the low end, stop-end red at the high end, and vice versa.
    for (let i = 0; i < 3; i++) {
      const t = (i - 1) * 0.25;
      push(new Vector3(low.x + across.x * t, low.y, low.z + across.z * t), 0.1, 1, 0.35);
      push(new Vector3(low.x - across.x * t, low.y, low.z - across.z * t), 1, 0.15, 0.15);
      push(new Vector3(high.x + across.x * t, high.y, high.z + across.z * t), 1, 0.15, 0.15);
      push(new Vector3(high.x - across.x * t, high.y, high.z - across.z * t), 0.1, 1, 0.35);
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('color', new BufferAttribute(colors, 3));

    const material = new PointsNodeMaterial();
    material.vertexColors = true;
    material.size = 2.6;
    material.sizeAttenuation = true;
    material.transparent = true;
    material.depthWrite = false;
    material.blending = AdditiveBlending;
    material.opacity = 0;

    this.disposables.push(geometry, material);
    this.lightMaterials.push(material);

    const points = new Points(geometry, material);
    points.name = `lights-${runway.lowIdent}`;
    points.frustumCulled = false;
    return points;
  }

  /**
   * PAPI: four lights beside the touchdown zone, white above the glideslope and red below.
   *
   * The colours are not decoration — they are the instrument. Each unit switches from white to
   * red at a slightly different angle (nominally 3.5°, 3.17°, 2.83°, 2.5°), so "two white two
   * red" means on slope and "four red" means you are about to arrive early. That means the
   * colours depend on where the *viewer* is, which is why they are recomputed every frame in
   * {@link updateFrame} rather than baked.
   */
  private buildPapi(runway: Runway): Group {
    const group = new Group();
    group.name = `papi-${runway.lowIdent}`;

    for (const end of ['low', 'high'] as const) {
      const threshold = this.thresholdPoint(runway, end, new Vector3());
      const other = this.thresholdPoint(runway, end === 'low' ? 'high' : 'low', new Vector3());
      const along = new Vector3().subVectors(other, threshold).normalize();
      const across = new Vector3(-along.z, 0, along.x);

      // 300 m in from the threshold, 15 m left of the edge, four units 9 m apart.
      const base = new Vector3()
        .copy(threshold)
        .addScaledVector(along, 300)
        .addScaledVector(across, -(runway.widthM / 2 + 15));

      for (let i = 0; i < 4; i++) {
        const position = new Vector3().copy(base).addScaledVector(across, -i * 9);
        const geometry = new BufferGeometry();
        const s = 1.1;
        geometry.setAttribute(
          'position',
          new BufferAttribute(
            new Float32Array([
              position.x - s, position.y + 0.9, position.z - s,
              position.x + s, position.y + 0.9, position.z - s,
              position.x + s, position.y + 0.9 + s * 2, position.z + s,
              position.x - s, position.y + 0.9 + s * 2, position.z + s,
            ]),
            3,
          ),
        );
        geometry.setIndex(new BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1));
        geometry.computeVertexNormals();

        const material = new MeshStandardNodeMaterial();
        material.color = new Color(0xffffff);
        material.emissive = new Color(0xffffff);
        material.emissiveIntensity = 0;
        material.side = DoubleSide;
        material.toneMapped = false;

        this.disposables.push(geometry, material);
        const mesh = new Mesh(geometry, material);
        mesh.frustumCulled = false;
        group.add(mesh);

        this.papiLights.push({ mesh, material });
        this.papi.push({ runway, end, position });
      }
    }
    return group;
  }

  /**
   * Update the lights.
   *
   * @param viewerLocal  the camera, in this airport's tangent frame (render axes).
   * @param sunElevationDeg  sun above the horizon; lights come up as it sets.
   */
  updateFrame(viewerLocal: Vector3, sunElevationDeg: number): void {
    // Lights fade in through civil twilight and are full on once the sun is below the horizon.
    const night = clamp((6 - sunElevationDeg) / 12, 0, 1);
    if (Math.abs(night - this.nightAmount) > 0.002) {
      this.nightAmount = night;
      for (const material of this.lightMaterials) material.opacity = night;
    }

    // PAPI: white above the unit's transition angle, red below.
    for (let i = 0; i < this.papiLights.length; i++) {
      const unit = this.papi[i];
      const light = this.papiLights[i];
      const toViewer = scratchToViewer.copy(viewerLocal).sub(unit.position);
      const horizontal = Math.hypot(toViewer.x, toViewer.z);
      const angleDeg = horizontal > 1 ? Math.atan2(toViewer.y, horizontal) * RAD2DEG : 90;

      const white = angleDeg >= PAPI_TRANSITION_DEG[i % 4];
      const material = light.material;
      const target = white ? 0xffffff : 0xff2a18;
      if (material.color.getHex() !== target) {
        material.color.setHex(target);
        material.emissive.setHex(target);
      }
      // PAPI is visible by day too — it is just much brighter relative to the scene at night.
      material.emissiveIntensity = 1.6 + this.nightAmount * 5;
    }
  }

  /** Place this airport under the current floating origin. */
  placeInto(originFrame: LocalFrame, position: Vector3, rotation: Quaternion): void {
    originFrame.transformFromRender(this.frame, position, rotation);
    this.group.position.copy(position);
    this.group.quaternion.copy(rotation);
    this.group.updateMatrix();
  }

  dispose(): void {
    for (const item of this.disposables) item.dispose();
    this.disposables.length = 0;
    this.group.clear();
  }
}

/**
 * Where to put an aircraft that is lined up on a runway threshold, ready to roll.
 *
 * Returns geodetic coordinates so the caller can place it under whatever origin is current.
 */
export function thresholdSpawn(
  runway: Runway,
  end: 'low' | 'high',
  groundClearanceM: number,
  alongRunwayM = 140,
): { latitudeDeg: number; longitudeDeg: number; altitudeM: number; headingDegTrue: number } {
  const headingDegTrue =
    end === 'low' ? runway.headingDegTrue : (runway.headingDegTrue + 180) % 360;
  const fromLat = end === 'low' ? runway.lowLatitudeDeg : runway.highLatitudeDeg;
  const fromLon = end === 'low' ? runway.lowLongitudeDeg : runway.highLongitudeDeg;
  const elevationM = end === 'low' ? runway.lowElevationM : runway.highElevationM;

  const point = project(fromLat, fromLon, headingDegTrue, alongRunwayM);
  return {
    latitudeDeg: point.latitudeDeg,
    longitudeDeg: point.longitudeDeg,
    // Clear of the pavement, and clear of the apron's few-centimetre lift.
    altitudeM: elevationM + groundClearanceM + 0.1,
    headingDegTrue,
  };
}

/**
 * Where to put an aircraft on a stabilised final approach.
 *
 * `distanceNm` back along the extended centreline, at the altitude a 3° glideslope puts it —
 * which is the approach every one of these runways is flown on.
 */
export function finalApproachSpawn(
  runway: Runway,
  end: 'low' | 'high',
  distanceNm = 3,
  glideslopeDeg = 3,
): { latitudeDeg: number; longitudeDeg: number; altitudeM: number; headingDegTrue: number } {
  const headingDegTrue =
    end === 'low' ? runway.headingDegTrue : (runway.headingDegTrue + 180) % 360;
  const thresholdLat = end === 'low' ? runway.lowLatitudeDeg : runway.highLatitudeDeg;
  const thresholdLon = end === 'low' ? runway.lowLongitudeDeg : runway.highLongitudeDeg;
  const elevationM = end === 'low' ? runway.lowElevationM : runway.highElevationM;

  const distanceM = distanceNm * 1_852;
  const behind = project(thresholdLat, thresholdLon, (headingDegTrue + 180) % 360, distanceM);

  return {
    latitudeDeg: behind.latitudeDeg,
    longitudeDeg: behind.longitudeDeg,
    altitudeM: elevationM + distanceM * Math.tan(glideslopeDeg * DEG2RAD),
    headingDegTrue,
  };
}

/** Point reached by travelling `distanceM` along a true bearing. */
function project(
  latitudeDeg: number,
  longitudeDeg: number,
  bearingDeg: number,
  distanceM: number,
): { latitudeDeg: number; longitudeDeg: number } {
  const R = 6_371_008.8;
  const angular = distanceM / R;
  const bearing = bearingDeg * DEG2RAD;
  const lat = latitudeDeg * DEG2RAD;
  const lon = longitudeDeg * DEG2RAD;

  const sinLat2 =
    Math.sin(lat) * Math.cos(angular) + Math.cos(lat) * Math.sin(angular) * Math.cos(bearing);
  const lat2 = Math.asin(clamp(sinLat2, -1, 1));
  const lon2 =
    lon +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angular) * Math.cos(lat),
      Math.cos(angular) - Math.sin(lat) * sinLat2,
    );

  return {
    latitudeDeg: lat2 * RAD2DEG,
    longitudeDeg: ((lon2 * RAD2DEG + 540) % 360) - 180,
  };
}
